import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { TableSkeleton } from '../../Components/common/Skeleton';
import { Plus } from 'lucide-react';
import HospitalRegisterModal from '../../Components/hospitals/HospitalRegisterModal';
import Pagination from '../../Components/common/Pagination';

const HospitalList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    city: '',
    state: '',
    specialty: '',
    verificationStatus: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    fetchHospitals();
  }, [pagination.page, filters]);

  const fetchHospitals = async () => {
    try {
      if (hospitals.length > 0) {
        setIsFetching(true);
      }
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...(filters.city && { city: filters.city }),
        ...(filters.state && { state: filters.state }),
        ...(filters.specialty && { specialty: filters.specialty }),
        ...(filters.verificationStatus && { verificationStatus: filters.verificationStatus }),
        ...(filters.search && { search: filters.search })
      });

      const response = await api.get(`/hospitals?${params}`);
      setHospitals(response.data.data.hospitals || []);
      setPagination((prev) => ({
        ...prev,
        total: response.data.data.pagination.total || 0,
        pages: response.data.data.pagination.pages || 0
      }));
    } catch (error) {
      toast.error('Failed to load hospitals');
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const getVerificationColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      verified: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="h-8 w-32 bg-slate-200 animate-pulse rounded-lg"></div>
        </div>
        <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 p-8">
          <TableSkeleton rows={8} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" style={{ minHeight: '80vh' }}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Hospitals</h1>
        <button 
          onClick={() => setIsRegisterModalOpen(true)}
          className="flex items-center justify-center gap-2 bg-[#143E78] text-white px-6 py-3 rounded-2xl font-black text-[11px] uppercase tracking-[0.2em] shadow-xl shadow-blue-900/10 hover:bg-blue-800 transition-all active:scale-[0.98]"
        >
          <Plus className="w-4 h-4" />
          Register Hospital
        </button>
      </div>

      <HospitalRegisterModal 
        isOpen={isRegisterModalOpen} 
        onClose={() => setIsRegisterModalOpen(false)} 
        onSuccess={fetchHospitals} 
      />

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="space-y-1">
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Search</label>
            <input
              type="text"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Search by name"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all text-sm font-medium"
            />
          </div>
          <div className="space-y-1">
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">City</label>
            <input
              type="text"
              name="city"
              value={filters.city}
              onChange={handleFilterChange}
              placeholder="Filter by city"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all text-sm font-medium"
            />
          </div>
          <div className="space-y-1 lg:block hidden">
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">State</label>
            <input
              type="text"
              name="state"
              value={filters.state}
              onChange={handleFilterChange}
              placeholder="Filter by state"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all text-sm font-medium"
            />
          </div>
          <div className="space-y-1">
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Specialty</label>
            <input
              type="text"
              name="specialty"
              value={filters.specialty}
              onChange={handleFilterChange}
              placeholder="Filter by specialty"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all text-sm font-medium"
            />
          </div>
          <div className="space-y-1">
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Status</label>
            <select
              name="verificationStatus"
              value={filters.verificationStatus}
              onChange={handleFilterChange}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all text-sm font-medium bg-white appearance-none"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="verified">Verified</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
      </div>

      {/* Hospitals Content */}
      <div 
        className={`bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden transition-opacity duration-300 ${isFetching ? 'opacity-40 pointer-events-none' : 'opacity-100'}`}
      >
        {/* Mobile View: Name, Mobile, View Details */}
        <div key={pagination.page} className="block lg:hidden divide-y divide-gray-100 animate-fadeIn">
          {hospitals.length > 0 ? (
            hospitals.map((hospital) => (
              <div key={hospital._id} className="p-5 space-y-4 hover:bg-gray-50 transition-colors">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <h3 className="text-sm font-bold text-gray-900 leading-tight">{hospital.hospitalName}</h3>
                    <p className="text-xs font-bold text-blue-600">{hospital.contactPerson?.phone || hospital.phone}</p>
                  </div>
                  <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${getVerificationColor(hospital.verificationStatus)}`}>
                    {hospital.verificationStatus}
                  </span>
                </div>
                <Link
                  to={`/hospitals/${hospital._id}`}
                  className="block w-full text-center py-2.5 bg-gray-50 border border-gray-200 text-xs font-bold text-gray-700 rounded-lg hover:bg-gray-100 active:scale-[0.98] transition-all"
                >
                  View Details
                </Link>
              </div>
            ))
          ) : (
            <div className="p-10 text-center text-gray-400 italic text-sm">
              No hospitals found
            </div>
          )}
        </div>

        {/* Desktop View: Full Table */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="sticky left-0 bg-gray-50 z-20 px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest border-r border-gray-200 shadow-sm">
                  Name
                </th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  Type
                </th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  Location
                </th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  Contact
                </th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  CC (VERIFIED BY)
                </th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  Verification
                </th>
              </tr>
            </thead>
            <tbody key={pagination.page} className="bg-white divide-y divide-gray-100 animate-fadeIn">
              {hospitals.length > 0 ? (
                hospitals.map((hospital) => (
                  <tr
                    key={hospital._id}
                    onClick={() => navigate(`/hospitals/${hospital._id}`)}
                    className="group hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <td className="sticky left-0 bg-white group-hover:bg-gray-50 z-10 transition-colors px-6 py-4 whitespace-nowrap border-r border-gray-200 shadow-sm">
                      <div className="text-sm font-bold text-gray-900">{hospital.hospitalName}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600 capitalize font-medium">{hospital.hospitalType}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600 font-medium">
                        {hospital.address?.city}, {hospital.address?.state}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-[11px] font-black text-slate-900">{hospital.contactPerson?.phone || hospital.phone}</div>
                      <div className="text-[10px] text-slate-500 lowercase font-medium">{hospital.contactPerson?.email || hospital.mailId}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[9px] font-black text-slate-400 uppercase">Inv:</span>
                          <span className="text-[10px] font-bold text-slate-700">
                            {hospital.user?.invitedBy ? `${hospital.user.invitedBy.firstName} ${hospital.user.invitedBy.lastName}` : 'System'}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[9px] font-black text-indigo-400 uppercase">Ver:</span>
                          <span className="text-[10px] font-bold text-indigo-700">
                            {hospital.verifiedBy ? `${hospital.verifiedBy.firstName} ${hospital.verifiedBy.lastName}` : 'Pending'}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${getVerificationColor(hospital.verificationStatus)}`}>
                        {hospital.verificationStatus}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-400 italic text-sm">
                    No hospitals found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>


      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-4">
          <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest order-2 sm:order-1">
            Displaying {hospitals.length > 0 ? (pagination.page - 1) * pagination.limit + 1 : 0}-{(pagination.page - 1) * pagination.limit + hospitals.length} of {pagination.total} records
          </div>
          <div className="order-1 sm:order-2 w-full sm:w-auto overflow-x-auto pb-2 sm:pb-0">
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.pages}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default HospitalList;
